import {
    BaseAdditionalActivity, BaseWorkout, WorkoutTimeFrame, CircuitAdditionalActivity, CircuitWorkout, TimerAdditionalActivity,
    WorkoutTypes, MssHyrox, RftWorkout,
    MssHyroxMainPart, WorkoutSyncInterface,
    MssWorkoutType
} from '@/types/workouts'
import { VuexModule, Module, Mutation, Action } from 'vuex-class-modules'
import store from '../index'
import runtimeProcessorModule from './runtime-processor.module'
import { TIMER_TYPE } from '@/types/timer'
import { CTRL_COMMAND_FEEDBACK, CTRL_COMMAND, FeedbackCtrlCmdMessage } from '@/types/socket'
import { ctrlCmdModule } from './control-commands.module'
import { TRAINING_TRIGGERED_BY } from '@/types/active-training'
import { mssWorkoutService } from '@/services/mss-workout.service'
import { mssHyroxWorkoutService } from '@/services/mss-hyrox.service'
import workoutsSyncManager from '../managers/workouts.sync.manager'


@Module
class MssHyroxActiveModule extends VuexModule implements WorkoutSyncInterface {

    public workoutPartIdx = 0
    public activeWorkoutTimeFramesArrayIdx = 0
    public triggeredBy: TRAINING_TRIGGERED_BY | null = null

    public warmingUps: BaseAdditionalActivity[] = []
    public coolingDowns: BaseAdditionalActivity[] = []
    public workout: MssHyrox | null = null

    private workoutCommandsWatcher: CallableFunction | null = null
    public theEndOfTimeFrameWatcher: CallableFunction | null = null
    public synchronizarionWatcher: CallableFunction | null = null

    get currentTimeSec(): number {

        const passedParts = ([] as (MssHyroxMainPart | BaseAdditionalActivity)[]).concat(
            this.warmingUps, (this.workout as MssHyrox).mainParts as MssHyroxMainPart[], this.coolingDowns
        ).slice(0, this.workoutPartIdx)

        const totalTimeProgress = passedParts.reduce((acc, workout) => {

            acc += workout.duration

            if ((workout as BaseAdditionalActivity).prep_time) {
                acc += (workout as BaseAdditionalActivity).prep_time
            }
            return acc
        }, 0)
        return totalTimeProgress + this.activeWorkoutTimeProgress
    }

    get elapsedTime(): number {

        let elapsedTime = this.currentTimeSec
        const { totalTime, currentTime, timerType } = runtimeProcessorModule

        if (totalTime > 0 || currentTime > 0) {
            let currentFrameElapsed = 0
            if (timerType === TIMER_TYPE.UP) {
                currentFrameElapsed = currentTime / 1000
            } else {
                currentFrameElapsed = (totalTime - currentTime) / 1000
            }
            elapsedTime -= currentFrameElapsed
        }

        return elapsedTime > 0 ? elapsedTime : 0
    }


    get timeFrames(): WorkoutTimeFrame[] {
        return this.activeWorkoutTimeFramesArray
            .filter((timeFrame: WorkoutTimeFrame, idx) => idx < this.activeWorkoutTimeFramesArrayIdx)
    }

    get activeWorkoutTimeProgress(): number {

        let time = 0

        if (this.timeFrames.length > 0) {

            time += this.timeFrames.map(timeFrame => timeFrame.duration)
            .reduce((acc, timeFrameDuration) => acc += timeFrameDuration)
        }

        const timeLeft = runtimeProcessorModule.totalTime - runtimeProcessorModule.currentTime
        if (timeLeft < runtimeProcessorModule.totalTime) {
            time += timeLeft / 1000
        }

        return time
    }

    get mainParts() {
        return this.workout?.mainParts as MssHyroxMainPart[]
    }

    get activeWorkoutPart(): BaseWorkout | BaseAdditionalActivity | MssHyroxMainPart | null {

        const warmingUpsPlusWorkout = (this.warmingUps.length + this.mainParts.length)

        if (this.workoutPartIdx < 0) {
            return null // workout isn't running
        } else if (this.workoutPartIdx < this.warmingUps.length) {

            return this.warmingUps[this.workoutPartIdx]

        } else if (this.workoutPartIdx < warmingUpsPlusWorkout) {
            return this.mainParts[this.workoutPartIdx - this.warmingUps.length] as MssHyroxMainPart

        } else {
            const coolingDownIdx = this.workoutPartIdx - warmingUpsPlusWorkout
            return this.coolingDowns[coolingDownIdx]
        }
    }

    get activeWorkoutTimeFramesArray(): WorkoutTimeFrame[] {

        let timeFrames: WorkoutTimeFrame[] = []

        if (this.activeWorkoutPart == null) {
            return timeFrames
        }

        if (this.isItAWarmingUp || this.isItACoolingDown) {

            if (this.isItACircuit) {

                timeFrames = mssWorkoutService.parseCircuitWuCdTimeFrames(this.activeWorkoutPart as CircuitAdditionalActivity)
            } else {
                timeFrames = mssWorkoutService.parseWuCdTimeFrames(this.activeWorkoutPart as BaseWorkout)
            }

        } else if ((this.activeWorkoutPart as MssHyroxMainPart).zones[0].type == MssWorkoutType.MSS_CIRCUIT){

            timeFrames = mssHyroxWorkoutService.parseCircuitTimeframes(this.activeWorkoutPart as MssHyroxMainPart)

            if (this.isItTheLastPart) {
                timeFrames.pop(); // remove the round rest time
            }
        } else {
            timeFrames = mssHyroxWorkoutService.parseTimeFrames(this.activeWorkoutPart as MssHyroxMainPart)
        }
        return timeFrames
    }

    get timeFrameCoordinates() {
        return { timeFrameIdx: this.activeWorkoutTimeFramesArrayIdx, workoutPartIdx: this.workoutPartIdx }
    }

    get activeTimeFrame(): WorkoutTimeFrame {
        return this.activeWorkoutTimeFramesArray[this.activeWorkoutTimeFramesArrayIdx]
    }

    get rounds(): number {

        if (this.isItACircuit) {
            return (this.activeWorkoutPart as CircuitWorkout).roundsData.length
        } else if (this.isItAnRft) {
            return (this.activeWorkoutPart as RftWorkout).rounds
        }
        return -1
    }

    get activeCircuitRoundIdx(): number {

        if (this.isItACircuit) {
            return this.activeTimeFrame.roundIdx as number
        }
        return 0
    }

    get isItTheLastRound(): boolean {
        return this.activeCircuitRoundIdx + 1 == this.rounds
    }

    get activeWorkoutPartName(): string {
        return (this.activeWorkoutPart as BaseWorkout)?.name
    }

    get isItAWarmingUp(): boolean {
        return this.workoutPartIdx < this.warmingUps.length
    }

    get isItLastWarmingUp(): boolean {
        return this.isItAWarmingUp && this.workoutPartIdx == this.warmingUps.length - 1
    }

    get isItACoolingDown(): boolean {
        return this.workoutPartIdx > this.warmingUps.length + this.mainParts.length - 1;
    }

    get isItARegularWorkout(): boolean {
        return !this.isItAWarmingUp && !this.isItACoolingDown
    }

    get isItATimer(): boolean {
        return !this.isItARegularWorkout && (this.activeWorkoutPart as TimerAdditionalActivity).timer > 0
    }

    get isItACircuit(): boolean {
        return (this.activeWorkoutPart as MssHyrox).type === WorkoutTypes.CIRCUIT
    }

    get isItAnRft(): boolean {
        return (this.activeWorkoutPart as MssHyrox).type === WorkoutTypes.RFT
    }

    get isItTheLastPart() {
        return (this.warmingUps.length + this.coolingDowns.length + this.mainParts.length) - (this.workoutPartIdx + 1) == 0;
    }

    get isItFirstTimeFrame(): boolean {
        return this.workoutPartIdx < 1 && this.activeWorkoutTimeFramesArrayIdx < 1
    }

    get isItTheLastTimeFrame(): boolean {
        return this.isItTheLastPart && (this.activeWorkoutTimeFramesArrayIdx >= this.activeWorkoutTimeFramesArray.length - 1)
    }

    get isItFirstTimeFrameInSet(): boolean {
        return this.activeWorkoutTimeFramesArrayIdx == 0
    }

    get isItTheLastTimeFrameInSet(): boolean {
        return this.activeWorkoutTimeFramesArrayIdx === this.activeWorkoutTimeFramesArray.length - 1
    }

    get isItTheMainWorkoutPreparationTime(): boolean {
        return (this.isItTheMainWorkoutActive && this.isItFirstTimeFrameInSet && !this.activeTimeFrame.restInterval && !this.activeTimeFrame.workInterval)
    }

    get isItPreparationTime(): boolean {

        return ((this.workoutPartIdx < 1 && this.isItFirstTimeFrameInSet) || 
            (this.isItAWarmingUp && this.isItTheLastTimeFrameInSet && this.workoutPartIdx < 1 && this.activeWorkoutTimeFramesArrayIdx > 1) ||
            (this.isItACoolingDown && this.isItFirstTimeFrameInSet) || this.isItTheMainWorkoutPreparationTime) 
            && this.activeWorkoutTimeFramesArray.length > 1
    }

    get isItTheMainWorkoutActive(): boolean {
        return !this.isItAWarmingUp && !this.isItACoolingDown
    }

    get totalTime(): number | undefined {
        return this.workout?.totalTime
    }

    @Mutation
    reset() {
        this.workoutPartIdx = 0
        this.activeWorkoutTimeFramesArrayIdx = 0

        this.workout = null
        this.warmingUps = []
        this.coolingDowns = []
        this.triggeredBy = null
    }

    @Mutation
    setTheEndOfTimeFrameWatcher(watcher: CallableFunction | null = null) {

        if (this.theEndOfTimeFrameWatcher) {
            this.theEndOfTimeFrameWatcher()
        }
        this.theEndOfTimeFrameWatcher = watcher
    }

    @Mutation
    setSyncWatcher(watcher: CallableFunction | null) {

        if (this.synchronizarionWatcher) {
            this.synchronizarionWatcher()
        }
        this.synchronizarionWatcher = watcher
    }

    @Action
    stopWatchingSync() {
        this.setSyncWatcher(null)
    }

    @Action
    stopWatchingTheEndOfTimeFrame() {
        this.setTheEndOfTimeFrameWatcher(null)
    }

    @Mutation
    setWorkoutCommandWatcher(watcher: CallableFunction | null = null) {
        if (this.workoutCommandsWatcher) {
            this.workoutCommandsWatcher()
        }
        this.workoutCommandsWatcher = watcher
    }

    @Mutation
    setTrriggeredBy(triggeredBy: TRAINING_TRIGGERED_BY) {
        this.triggeredBy = triggeredBy
    }

    @Mutation
    parse(workout: MssHyrox) {

        this.workoutPartIdx = 0
        this.activeWorkoutTimeFramesArrayIdx = 0

        // eslint-disable-next-line
        const { warming_ups, cooling_downs, ...mainPart } = workout

        // eslint-disable-next-line
        this.warmingUps = warming_ups
        // eslint-disable-next-line
        this.coolingDowns = cooling_downs.map(coolingDown => Object.assign({}, coolingDown))

        this.workout = mainPart as MssHyrox
    }

    @Mutation
    setTimeFrameIdx(idx: number) {
        this.activeWorkoutTimeFramesArrayIdx = idx
    }

    @Mutation
    setWorkoutPartIdx(idx: number) {
        this.workoutPartIdx = idx
    }

    @Action
    loadTheNextTimeFrame() {

        if (this.activeWorkoutTimeFramesArrayIdx < this.activeWorkoutTimeFramesArray.length - 1) {

            this.setTimeFrameIdx(this.activeWorkoutTimeFramesArrayIdx + 1)

        } else if (!this.isItTheLastTimeFrame) {

            this.setTimeFrameIdx(0)
            this.setWorkoutPartIdx(this.workoutPartIdx + 1)
        }
    }

    @Action
    init(arg: { workout: MssHyrox; triggeredBy: TRAINING_TRIGGERED_BY }) {

        this.resetToDefault()
        this.setSyncWatcher(ctrlCmdModule.$watch(module => module.workoutCmdLastFeedback, lastMessage => {

            if (lastMessage?.event === CTRL_COMMAND_FEEDBACK.WORKOUT_GOT_FORWARD) {
                this.sync(lastMessage)
                ctrlCmdModule.cmdDisposal(lastMessage.event)
            }
        }))

        this.parse(arg.workout)
        this.setTrriggeredBy(arg.triggeredBy)
    }

    @Action
    timeFrameStart() {

        this.watchTheEndOfTimeFrame()

        let duration = this.activeTimeFrame.duration
        
        if (duration > 0) {
            if (this.isItPreparationTime) {
                runtimeProcessorModule.changeTimerType(TIMER_TYPE.DOWN)
            } else if ((this.activeWorkoutPart as BaseWorkout).type == WorkoutTypes.RFT) {
                duration = 0
                runtimeProcessorModule.changeTimerType(TIMER_TYPE.UP)
            } else {
                runtimeProcessorModule.changeTimerType(TIMER_TYPE.DOWN)
            }
            runtimeProcessorModule.start(duration)
        }  else if (!this.isItTheLastTimeFrame){

            this.loadTheNextTimeFrame()
            this.timeFrameStart()
        }
    }

    @Action
    watchTheEndOfTimeFrame() {

        this.stopWatchingTheEndOfTimeFrame()

        const theEndOfTimeFrameWatcher = runtimeProcessorModule.$watch(timerModule => timerModule.status, status => {

            const { stopped, finished } = status

            if (stopped) {
                this.stop()
            }
            else if (finished) { // finished automatically

                if (!this.isItTheLastTimeFrame) {

                    this.loadTheNextTimeFrame()
                    this.timeFrameStart()

                } else {
                    this.stop()
                }
            }
        })
        this.setTheEndOfTimeFrameWatcher(theEndOfTimeFrameWatcher)
    }

    @Action
    start() {

        return new Promise((resolve, reject) => {

            const rejectionTimeout = setTimeout(() => reject(), 2000)

            if (this.triggeredBy == TRAINING_TRIGGERED_BY.USER) {

                this.setWorkoutCommandWatcher(ctrlCmdModule.$watch(module => module.workoutCmdLastFeedback, lastMessage => {
    
                    if (lastMessage?.event === CTRL_COMMAND_FEEDBACK.WORKOUT_STARTED) {
   
                        window.clearTimeout(rejectionTimeout)
    
                        this.timeFrameStart()
    
                        ctrlCmdModule.cmdDisposal(lastMessage.event)
                    }
                }))
    
                const { id, totalTime, type } = this.workout as BaseWorkout
                const value = { id, time: totalTime }
                let trigger = CTRL_COMMAND.MSS_HYROX_START
                if (type == WorkoutTypes.VELOCITY) {
                    trigger = CTRL_COMMAND.MSS_VELOCITY_START
                }
                ctrlCmdModule.triggerCtrlCmd({ trigger, value })
    
            } else {
    
                this.timeFrameStart()
                resolve(null)
            }
        })
    }

    @Action
    pause() {

        runtimeProcessorModule.pause()

        this.setWorkoutCommandWatcher(ctrlCmdModule.$watch(module => module.workoutCmdLastFeedback, lastMessage => {

            if (lastMessage?.event === CTRL_COMMAND_FEEDBACK.WORKOUT_PAUSED) {

                this.sync(lastMessage)

                ctrlCmdModule.cmdDisposal(lastMessage.event)
            }
        }))

        ctrlCmdModule.triggerCtrlCmd({
            trigger: CTRL_COMMAND.WORKOUT_PAUSE, value: {
                id: this.workout?.id as string,
                time: this.currentTimeSec
            }
        })
    }

    @Action
    stop() {

        this.stopWatchingTheEndOfTimeFrame()

        if (runtimeProcessorModule.isItInProcess) {
            runtimeProcessorModule.stop()
        }

        this.setWorkoutCommandWatcher(ctrlCmdModule.$watch(module => module.workoutCmdLastFeedback, lastMessage => {

            if (lastMessage?.event === CTRL_COMMAND_FEEDBACK.WORKOUT_STOPPED) {

                ctrlCmdModule.cleanUp()

                this.resetToDefault()

                ctrlCmdModule.cmdDisposal(lastMessage.event)
            }
        }))
    }

    @Action
    resume() {

        runtimeProcessorModule.resume()

        this.setWorkoutCommandWatcher(ctrlCmdModule.$watch(module => module.workoutCmdLastFeedback, lastMessage => {

            if (lastMessage?.event === CTRL_COMMAND_FEEDBACK.WORKOUT_RESUMED) {

                ctrlCmdModule.cmdDisposal(lastMessage.event)
            }
        }))

        ctrlCmdModule.triggerCtrlCmd({
            trigger: CTRL_COMMAND.WORKOUT_RESUME, value: {
                id: this.workout?.id as string,
                time: this.currentTimeSec,
            }
        })
    }

    @Action
    forward() {

        if (!this.isItPreparationTime && this.isItTheLastTimeFrame) {

            this.stop()

        } else {

            const { id } = this.workout as BaseWorkout

            ctrlCmdModule.triggerCtrlCmd({
                trigger: CTRL_COMMAND.WORKOUT_FORWARD, value: { id }
            })
        }
    }

    @Action
    backward() {

        this.setWorkoutCommandWatcher(ctrlCmdModule.$watch(module => module.workoutCmdLastFeedback, lastMessage => {

            if (lastMessage?.event === CTRL_COMMAND_FEEDBACK.WORKOUT_GOT_BACKWARD) {

                this.sync(lastMessage)

                ctrlCmdModule.cmdDisposal(lastMessage.event)
            }
        }))

        const { id } = this.workout as BaseWorkout

        ctrlCmdModule.triggerCtrlCmd({
            trigger: CTRL_COMMAND.WORKOUT_BACKWARD, value: { id }
        })
    }

    @Action
    sync(workoutSyncCmd: FeedbackCtrlCmdMessage) {

        if (workoutSyncCmd != undefined) {

            const { value } = workoutSyncCmd
            const { id, time } = value
            if (id == this.workout?.id && time != undefined) {
                this.syncWithTime(time)
            }
            
            ctrlCmdModule.cmdDisposal(workoutSyncCmd.event)
        }
    }
    
    @Action
    syncWithTime(time: number) {

        if (this.currentTimeSec != time) {

            workoutsSyncManager.sync(time, this)
        }
    }

    @Action
    resetToDefault() {
        this.reset()
        this.stopWatchingSync()
        this.setWorkoutCommandWatcher()
        this.stopWatchingTheEndOfTimeFrame()
    }
}

export default new MssHyroxActiveModule({ store, name: 'mssHyroxActive' })
